import { useQuery } from '@apollo/client';
import { LoadingOrError } from '@retainerclub/common';
import { GET_PRICE_BOOK_PRODUCTS } from '@retainerclub/customer-api';
import { useAccount } from '@retainerclub/hooks';
import { useMemo } from 'react';
import DashboardLayoutTitleWithPartner from '../../layouts/DashboardLayout/DashboardLayoutTitleWithPartner';
import ShopFilters from './ShopFilters';
import ShopProducts from './ShopProducts';

function Shop() {
  const { currentPatient } = useAccount();

  const { data, error, loading } = useQuery(GET_PRICE_BOOK_PRODUCTS);

  // Derive the ordered list directly from the query result during render.
  // Mirroring this into state via onCompleted breaks on SPA navigation: as of
  // Apollo 3.8 onCompleted does not fire when the query resolves from the cache,
  // so the local copy stayed empty/stale until a full page refresh forced a
  // network fetch.
  const filteredProducts = useMemo(() => {
    const pBP = data?.priceBookProducts;

    if (!pBP) {
      return [];
    }

    const allRetainers = pBP.filter((p) =>
      p.product.productCategories.some((c) => c.name === 'Retainers'),
    );

    // Enrolled patients can buy any retainer (their subscription retainer plus
    // à-la-carte ones like single-arch). Non-enrolled patients only see the
    // à-la-carte retainers, not the subscription-only ones.
    const retainers = currentPatient?.hasPlan
      ? allRetainers
      : allRetainers.filter(
          (r) => r.product.defaultSubscriptionRetainer === false,
        );

    return [
      ...pBP.filter((p) => p.product.productType === 'plan'),
      ...retainers,
      ...pBP
        .filter(
          (p) =>
            p.product.productType !== 'plan' &&
            p.product.productCategories.every((c) => c.name !== 'Retainers'),
        )
        .sort((a, b) => a.product.name.localeCompare(b.product.name)),
    ];
  }, [data?.priceBookProducts, currentPatient?.hasPlan]);

  const shouldRender = LoadingOrError({
    loading,
    error,
    data,
    dataKey: 'priceBookProducts',
  });

  return (
    <div className="bg-white">
      <main className="relative">
        <DashboardLayoutTitleWithPartner
          heading="Shop for"
          title={currentPatient?.firstName}
          subtitle="Shop the latest products offered by your provider"
        />
        {shouldRender ? (
          <>
            <ShopFilters />

            <ShopProducts priceBookProducts={filteredProducts} />
          </>
        ) : (
          shouldRender
        )}
      </main>
    </div>
  );
}

export default Shop;
